import { useState, useMemo } from "react";
import usePageLoader from '../../hooks/usePageLoader';
import { ShimmerStatCard, ShimmerBox, ShimmerTable } from '../../components/common/Shimmer';
import {
  FiRefreshCw, FiTrash2, FiZap, FiSearch, FiX, FiEye, FiEyeOff,
} from "react-icons/fi";
import toast from "react-hot-toast";
import StatusBadge from "../../components/common/StatusBadge";
import Pagination from "../../components/common/Pagination";

const MOCK_ARTICLES = [
  { id: 1, title: "How to reset your password", category: "Account", status: "Trained", lastTrained: "2 days ago", chars: 3200 },
  { id: 2, title: "Getting started guide", category: "Onboarding", status: "Trained", lastTrained: "1 day ago", chars: 11400 },
  { id: 3, title: "Billing and subscription FAQ", category: "Billing", status: "Pending", lastTrained: "—", chars: 7800 },
  { id: 4, title: "Contact support options", category: "Support", status: "Trained", lastTrained: "3 days ago", chars: 2100 },
  { id: 5, title: "Integrating with third-party tools", category: "Integrations", status: "Pending", lastTrained: "—", chars: 9600 },
  { id: 6, title: "Understanding your invoice", category: "Billing", status: "Failed", lastTrained: "5 days ago", chars: 4500 },
];

const PAGE_SIZE = 8;
const STATUS_TABS = ["All", "Trained", "Pending", "Failed"];

function fmtChars(n) {
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  return String(n);
}

function simulateRetrain(ids, setArticles, cb) {
  setArticles((prev) =>
    prev.map((a) => (ids.includes(a.id) ? { ...a, status: "Processing" } : a))
  );
  setTimeout(() => {
    setArticles((prev) =>
      prev.map((a) =>
        ids.includes(a.id) ? { ...a, status: "Trained", lastTrained: "just now" } : a
      )
    );
    cb?.();
  }, 2000);
}

function KpiCard({ label, value, sub, color = "text-gray-900" }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function ContentPageSkeleton() {
  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 px-6 py-5 space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <ShimmerStatCard key={i} />)}
      </div>
      <div className="flex items-center gap-3">
        <ShimmerBox h="h-9" w="w-56" rounded="rounded-lg" />
        <div className="flex gap-2">
          {[...Array(4)].map((_, i) => <ShimmerBox key={i} h="h-8" w="w-20" rounded="rounded-lg" />)}
        </div>
      </div>
      <ShimmerTable rows={8} cols={6} />
    </div>
  );
}

export default function ZendeskContent() {
  const [subdomain, setSubdomain] = useState("");
  const loading = usePageLoader();
  const [email, setEmail] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [connected, setConnected] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [articles, setArticles] = useState(MOCK_ARTICLES);
  const [search, setSearch] = useState("");
  const [statusTab, setStatusTab] = useState("All");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);

  const handleConnect = () => {
    if (!subdomain.trim() || !email.trim() || !apiToken.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    setConnected(true);
    toast.success("Zendesk Help Center connected!");
  };

  const handleSync = () => {
    setSyncing(true);
    setTimeout(() => {
      setSyncing(false);
      toast.success("Synced latest articles from Zendesk.");
    }, 1800);
  };

  const handleDisconnect = () => {
    setConnected(false);
    setArticles(MOCK_ARTICLES);
    setSelected([]);
    setSearch("");
    setStatusTab("All");
    toast("Zendesk disconnected.", { icon: "🟩" });
  };

  const filtered = useMemo(() => {
    let list = articles;
    if (statusTab !== "All") list = list.filter((a) => a.status === statusTab);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.category.toLowerCase().includes(q)
      );
    }
    return list;
  }, [articles, statusTab, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const kpi = useMemo(() => {
    const trained = articles.filter((a) => a.status === "Trained").length;
    const pending = articles.filter((a) => a.status === "Pending" || a.status === "Failed").length;
    const categories = [...new Set(articles.map((a) => a.category))].length;
    const totalChars = articles.reduce((s, a) => s + (a.chars || 0), 0);
    return { trained, pending, categories, totalChars };
  }, [articles]);

  const pendingIds = articles
    .filter((a) => a.status === "Pending" || a.status === "Failed")
    .map((a) => a.id);

  const allChecked =
    paginated.length > 0 && paginated.every((a) => selected.includes(a.id));
  const toggleAll = () =>
    setSelected(
      allChecked
        ? selected.filter((id) => !paginated.find((a) => a.id === id))
        : [...new Set([...selected, ...paginated.map((a) => a.id)])]
    );
  const toggleOne = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const handleBulkRetrain = () => {
    simulateRetrain(selected, setArticles, () => {
      toast.success(`Retraining ${selected.length} article(s)…`);
      setSelected([]);
    });
  };
  const handleBulkDelete = () => {
    setArticles((prev) => prev.filter((a) => !selected.includes(a.id)));
    toast.success(`Removed ${selected.length} article(s).`);
    setSelected([]);
  };
  const handleRetrain = (id) =>
    simulateRetrain([id], setArticles, () =>
      toast.success("Article queued for retraining.")
    );
  const handleDelete = (id) => {
    setArticles((prev) => prev.filter((a) => a.id !== id));
    toast.success("Article removed.");
  };
  const handleTrainPending = () => {
    if (!pendingIds.length) return;
    simulateRetrain(pendingIds, setArticles, () =>
      toast.success(`Training ${pendingIds.length} pending article(s)…`)
    );
  };

  return (
    <div className="-m-6 flex flex-col" style={{ height: "calc(100vh - 4rem)" }}>

      {/* Sticky header — always visible */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-gray-900">Zendesk</h1>
              {connected && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                  Connected
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              {connected
                ? subdomain ? `${subdomain}.zendesk.com` : "yourcompany.zendesk.com"
                : "Connect your Zendesk Help Center"}
            </p>
          </div>
          {connected && (
            <div className="flex items-center gap-2">
              {pendingIds.length > 0 && (
                <button
                  onClick={handleTrainPending}
                  className="flex items-center gap-1.5 text-sm bg-amber-50 text-amber-700 border border-amber-200 px-3 py-1.5 rounded-lg hover:bg-amber-100 transition-colors"
                >
                  <FiZap size={13} /> Train {pendingIds.length} Pending
                </button>
              )}
              <button
                onClick={handleSync}
                disabled={syncing}
                className="flex items-center gap-1.5 text-sm bg-primary text-white px-3 py-1.5 rounded-lg hover:opacity-90 disabled:opacity-60 transition-opacity"
              >
                <FiRefreshCw size={13} className={syncing ? "animate-spin" : ""} />
                {syncing ? "Syncing…" : "Sync Articles"}
              </button>
              <button
                onClick={handleDisconnect}
                className="flex items-center gap-1.5 text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-1.5 rounded-lg hover:bg-red-100 transition-colors"
              >
                Disconnect
              </button>
            </div>
          )}
        </div>
      </div>

      {!connected ? (
        /* Connect form */
        <div className="flex-1 overflow-y-auto bg-gray-50 flex items-center justify-center p-8">
          <div className="bg-white border border-gray-200 rounded-2xl p-8 w-full max-w-md shadow-sm">
            <p className="text-sm text-gray-500 mb-5">
              Train your chatbot on Zendesk Help Center articles automatically.
            </p>

            <ol className="bg-gray-50 rounded-xl p-4 mb-6 space-y-1.5 text-sm text-gray-600 list-decimal list-inside">
              <li>Log in to your Zendesk account as an admin</li>
              <li>Go to <b>Admin → Apps &amp; Integrations → Zendesk API</b></li>
              <li>Enable token access and create a new API token</li>
              <li>Enter your subdomain, email, and token below</li>
            </ol>

            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subdomain</label>
                <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 ring-primary">
                  <input
                    type="text"
                    value={subdomain}
                    onChange={(e) => setSubdomain(e.target.value)}
                    placeholder="yourcompany"
                    className="flex-1 px-3 py-2 text-sm outline-none"
                  />
                  <span className="px-3 py-2 bg-gray-100 text-gray-500 text-sm border-l border-gray-300 shrink-0">
                    .zendesk.com
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@company.com"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Token</label>
                <div className="relative">
                  <input
                    type={showToken ? "text" : "password"}
                    value={apiToken}
                    onChange={(e) => setApiToken(e.target.value)}
                    placeholder="Your Zendesk API token"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 ring-primary"
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showToken ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                  </button>
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-400 mb-5">
              Find API tokens at{" "}
              <b>{subdomain || "yourcompany"}.zendesk.com/admin/apps-integrations/apis/zendesk-api</b>
            </p>

            <button
              onClick={handleConnect}
              className="w-full bg-primary text-white py-2.5 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Connect Zendesk
            </button>
          </div>
        </div>
      ) : loading ? <ContentPageSkeleton /> : (
        /* Connected — article management */
        <div className="flex-1 overflow-y-auto bg-gray-50 px-6 py-5 space-y-5">
          {/* KPI cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KpiCard label="Total Articles" value={articles.length} sub="from Zendesk" />
            <KpiCard
              label="Trained"
              value={kpi.trained}
              color="text-green-600"
              sub="ready to answer"
            />
            <KpiCard
              label="Pending / Failed"
              value={kpi.pending}
              color={kpi.pending > 0 ? "text-amber-600" : "text-gray-900"}
              sub="need training"
            />
            <KpiCard
              label="Categories"
              value={kpi.categories}
              sub={`${fmtChars(kpi.totalChars)} total chars`}
            />
          </div>

          {/* Search + filter */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <FiSearch
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search articles or categories…"
                className="w-full pl-8 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 ring-primary"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FiX size={14} />
                </button>
              )}
            </div>
            <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1 self-start">
              {STATUS_TABS.map((t) => (
                <button
                  key={t}
                  onClick={() => { setStatusTab(t); setPage(1); }}
                  className={`text-xs px-3 py-1 rounded-md transition-colors ${
                    statusTab === t
                      ? "bg-primary text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Bulk action bar */}
          {selected.length > 0 && (
            <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl px-4 py-2.5">
              <span className="text-sm text-primary font-medium">{selected.length} selected</span>
              <div className="flex-1" />
              <button
                onClick={handleBulkRetrain}
                className="flex items-center gap-1.5 text-sm bg-primary text-white px-3 py-1.5 rounded-lg hover:opacity-90"
              >
                <FiZap size={13} /> Retrain Selected
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-1.5 text-sm text-red-600 border border-red-200 bg-white px-3 py-1.5 rounded-lg hover:bg-red-50"
              >
                <FiTrash2 size={13} /> Delete Selected
              </button>
              <button onClick={() => setSelected([])} className="text-gray-400 hover:text-gray-600 ml-1">
                <FiX size={15} />
              </button>
            </div>
          )}

          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {paginated.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center text-2xl mb-3">
                  🟩
                </div>
                <p className="text-sm font-medium text-gray-700">No articles found</p>
                <p className="text-xs text-gray-400 mt-1">
                  {search || statusTab !== "All"
                    ? "Try adjusting your filters"
                    : "Sync Zendesk to import Help Center articles"}
                </p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="pl-4 pr-2 py-3 w-8">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={toggleAll}
                        className="rounded accent-primary"
                      />
                    </th>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Title</th>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Category</th>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Chars</th>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Last Trained</th>
                    <th className="py-3 pr-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginated.map((article) => (
                    <tr key={article.id} className="hover:bg-gray-50 transition-colors">
                      <td className="pl-4 pr-2 py-3">
                        <input
                          type="checkbox"
                          checked={selected.includes(article.id)}
                          onChange={() => toggleOne(article.id)}
                          className="rounded accent-primary"
                        />
                      </td>
                      <td className="py-3 pr-4">
                        <span
                          className="font-medium text-gray-900 truncate block max-w-xs"
                          title={article.title}
                        >
                          {article.title}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium">
                          {article.category}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <StatusBadge status={article.status} />
                      </td>
                      <td className="py-3 pr-4 text-gray-500">{fmtChars(article.chars)}</td>
                      <td className="py-3 pr-4 text-gray-500">{article.lastTrained}</td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleRetrain(article.id)}
                            disabled={article.status === "Processing"}
                            title="Retrain"
                            className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg disabled:opacity-40 transition-colors"
                          >
                            <FiRefreshCw
                              size={14}
                              className={article.status === "Processing" ? "animate-spin" : ""}
                            />
                          </button>
                          <button
                            onClick={() => handleDelete(article.id)}
                            title="Remove"
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination footer */}
          {filtered.length > 0 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–
                {Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} articles
              </p>
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
